import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Layout } from "../components/Layout";
import { Heart, HandHeart, Sparkle, CheckCircle, Quotes } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const PRESETS = [
  { amount: 500, label: "₹500", impact: "1 month of platform hosting for 1 worker" },
  { amount: 2500, label: "₹2,500", impact: "1 worker's full skill verification" },
  { amount: 10000, label: "₹10,000", impact: "2 workers + 1 MSME verified" },
  { amount: 50000, label: "₹50,000", impact: "10-worker hometown cohort" },
];

export default function Donate() {
  const [params] = useSearchParams();
  const initialProgram = params.get("program");
  const initialWorker = params.get("worker");

  const [amount, setAmount] = useState(2500);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [target, setTarget] = useState(initialProgram ? "program" : initialWorker ? "worker" : "general");
  const [targetId, setTargetId] = useState(initialProgram || initialWorker || "");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/donations/stats").then(({ data }) => setStats(data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/donations", {
        amount: Number(amount),
        currency: "INR",
        donor_name: name || (anonymous ? "Anonymous patron" : "Friend of the platform"),
        donor_email: email,
        donor_message: msg,
        target,
        target_id: targetId || null,
        anonymous,
      });
      setSuccess(data);
      toast.success("Thank you! Your contribution is recorded.");
      api.get("/donations/stats").then(({ data }) => setStats(data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record donation");
    } finally { setBusy(false); }
  };

  if (success) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-50 grid place-items-center mx-auto">
            <CheckCircle size={48} weight="duotone" color="#16A34A" />
          </div>
          <h1 className="text-3xl font-bold text-[var(--as-primary)] mt-6" style={{fontFamily:"Inter"}}>Thank you, {success.donor_name}.</h1>
          <p className="text-[var(--as-text-secondary)] mt-2">
            ₹{success.amount.toLocaleString("en-IN")} recorded. Reference: <code className="text-xs">{success.id}</code>
          </p>
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 mt-6 max-w-md mx-auto">
            <strong>MOCK PAYMENT</strong> — secure Stripe / Razorpay payment is being wired. For now we've stored your donation intent so you can re-confirm once payments are live.
          </p>
          <div className="flex gap-3 justify-center mt-8">
            <a href="/donate" className="btn-secondary">Donate again</a>
            <a href="/sponsorship-programs" className="btn-primary">Browse programs</a>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Toaster richColors position="top-center" />
      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Hero */}
        <div className="grid md:grid-cols-5 gap-8 items-start">
          <div className="md:col-span-3">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)] flex items-center gap-2">
              <Sparkle size={14} weight="fill" /> Power a Worker
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] text-[var(--as-primary)] mt-3" style={{fontFamily:"Inter"}} data-testid="donate-hero">
              Don't keep us running.<br />
              <span className="text-[var(--as-accent)]">Power a worker instead.</span>
            </h1>
            <p className="text-lg text-[var(--as-text-secondary)] mt-5 leading-relaxed">
              Every rupee here directly funds skill verification, badges, and discoverability for a real
              worker or MSME. The platform is funded separately by sponsors and revenue — your money
              goes to the people, not to our servers.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-3">
              <div className="as-card p-4">
                <div className="text-2xl font-extrabold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>100%</div>
                <div className="text-xs uppercase tracking-wider font-bold text-[var(--as-text-secondary)] mt-1">Goes to workers</div>
              </div>
              <div className="as-card p-4">
                <div className="text-2xl font-extrabold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{stats ? stats.count : "—"}</div>
                <div className="text-xs uppercase tracking-wider font-bold text-[var(--as-text-secondary)] mt-1">Patrons so far</div>
              </div>
              <div className="as-card p-4">
                <div className="text-2xl font-extrabold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>₹{stats ? stats.total_amount.toLocaleString("en-IN") : "0"}</div>
                <div className="text-xs uppercase tracking-wider font-bold text-[var(--as-text-secondary)] mt-1">Raised</div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="as-card p-6 md:col-span-2 space-y-4" data-testid="donate-form">
            <div className="flex items-center gap-2 mb-1">
              <Heart size={20} weight="duotone" color="#00B7A6" />
              <span className="font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>Make a contribution</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {PRESETS.map((p) => (
                <button key={p.amount} type="button" onClick={() => setAmount(p.amount)}
                  className={`text-left p-3 rounded-xl border-2 transition-all ${Number(amount) === p.amount ? "border-[var(--as-accent)] bg-[var(--as-bg-alt)]" : "border-[var(--as-border)] hover:bg-[var(--as-bg-alt)]"}`}
                  data-testid={`donate-preset-${p.amount}`}>
                  <div className="font-extrabold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{p.label}</div>
                  <div className="text-[11px] text-[var(--as-text-secondary)] mt-0.5">{p.impact}</div>
                </button>
              ))}
            </div>

            <div>
              <label className="as-label">Custom amount (₹)</label>
              <input className="as-input" type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="donate-amount" />
            </div>

            <div>
              <label className="as-label">Where should it go?</label>
              <select className="as-input" value={target} onChange={(e) => { setTarget(e.target.value); setTargetId(""); }} data-testid="donate-target">
                <option value="general">Wherever it's needed most</option>
                <option value="worker">A specific worker</option>
                <option value="program">A specific sponsorship program</option>
              </select>
            </div>

            {target !== "general" && (
              <div>
                <label className="as-label">{target === "worker" ? "Worker ID" : "Program ID"}</label>
                <input className="as-input" value={targetId} onChange={(e) => setTargetId(e.target.value)} placeholder={target === "worker" ? "u-demo-ravi" : "prog-demo-..."} data-testid="donate-target-id" />
              </div>
            )}

            <div>
              <label className="as-label">Your name</label>
              <input className="as-input" value={name} onChange={(e) => setName(e.target.value)} required data-testid="donate-name" />
            </div>
            <div>
              <label className="as-label">Email (for receipt)</label>
              <input className="as-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="donate-email" />
            </div>
            <div>
              <label className="as-label">Message (optional)</label>
              <textarea className="as-input min-h-[60px]" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="donate-msg" />
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="w-4 h-4" data-testid="donate-anon" />
              <span className="text-[var(--as-text-secondary)]">Show me as "Anonymous patron"</span>
            </label>

            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              <strong>MOCK PAYMENT</strong> · We're wiring secure Stripe / Razorpay. For now your intent is recorded and we'll email you the final link.
            </div>

            <button type="submit" disabled={busy || !amount || !name || !email} className="btn-primary w-full" data-testid="donate-submit">
              <Heart size={18} weight="fill" /> {busy ? "..." : `Contribute ₹${Number(amount || 0).toLocaleString("en-IN")}`}
            </button>
          </form>
        </div>

        {/* Recent donations */}
        {stats && stats.recent.length > 0 && (
          <div className="mt-16">
            <h2 className="text-xl font-bold text-[var(--as-primary)] mb-4" style={{fontFamily:"Inter"}}>Recent patrons</h2>
            <ul className="space-y-3">
              {stats.recent.map((d) => (
                <li key={d.id} className="as-card p-4 flex items-start gap-3" data-testid={`recent-donation-${d.id}`}>
                  <HandHeart size={22} weight="duotone" color="#00B7A6" className="shrink-0 mt-0.5" />
                  <div className="flex-1 flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-bold text-[var(--as-primary)]">{d.donor_name}</div>
                      {d.donor_message && (
                        <p className="text-sm text-[var(--as-text-secondary)] italic mt-0.5">
                          <Quotes size={12} weight="duotone" className="inline mr-1" />{d.donor_message}
                        </p>
                      )}
                    </div>
                    <span className="font-extrabold text-[var(--as-accent)]" style={{fontFamily:"Inter"}}>
                      ₹{d.amount.toLocaleString("en-IN")}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
}
